import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useForm, FormProvider } from 'react-hook-form';
import Modal from './Modal';
import InputProduct from '../Form/Fields/InputProduct';
import Textarea from '../Form/Fields/Textarea';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import './ExchangeModal.scss';
import { API_BASE_URL } from '../../api';

export default function SendExchangeModal({ show, onClose, targetPharmacy }) {
  const methods = useForm();
  const [lines, setLines] = useState([{ id: 'l0', orderableId: null, quantity: 1 }]);
  const [submitting, setSubmitting] = useState(false);
  const lineCounter = useRef(1);

  useEffect(() => {
    if (!show) return;
    setLines([{ id: 'l0', orderableId: null, quantity: 1 }]);
    lineCounter.current = 1;
    methods.reset();
  }, [show]);

  const addLine = () => {
    const id = `l${lineCounter.current++}`;
    setLines(prev => [...prev, { id, orderableId: null, quantity: 1 }]);
  };

  const removeLine = (id) => {
    setLines(prev => prev.filter(l => l.id !== id));
  };

  const updateLine = (id, field, value) => {
    setLines(prev => prev.map(l => l.id === id ? { ...l, [field]: value } : l));
  };

  const handleSubmit = async () => {
    const validLines = lines.filter(l => l.orderableId && Number(l.quantity) > 0);
    if (!validLines.length) {
      toast.error('Ajoutez au moins un produit');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE_URL}/api/v1/exchanges`, {
        toPharmacyId: targetPharmacy.id,
        notes: methods.getValues('notes'),
        items: validLines.map(l => ({
          orderableId: Number(l.orderableId),
          quantity: Number(l.quantity),
        })),
      }, { withCredentials: true });

      toast.success(`Produits envoyés à ${targetPharmacy.name}`);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      title={`Envoyer des produits à ${targetPharmacy?.name ?? ''}`}
      classSize="exchange-modal-dialog"
      icon={<FontAwesomeIcon icon={faPaperPlane} />}
      iconBg="#dbeafe"
      iconColor="#2563eb"
      footer={
        <button
          className="modal-btn-primary"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting
            ? <><span className="spinner-border spinner-border-sm me-2" />Envoi...</>
            : <><FontAwesomeIcon icon={faPaperPlane} className="me-2" />Envoyer</>
          }
        </button>
      }
    >
      <FormProvider {...methods}>
        <div className="exchange-modal">
          <div className="exchange-modal__lines-header">
            <span className="exchange-modal__col-product">Produit</span>
            <span className="exchange-modal__col-qty">Quantité</span>
            <span className="exchange-modal__col-action" />
          </div>

          <div className="mb-3">
            {lines.map((line) => (
              <div key={line.id} className="exchange-modal__line">
                <div className="exchange-modal__col-product">
                  <InputProduct
                    name={line.id}
                    handleChange={(selected) => updateLine(line.id, 'orderableId', selected?.value ?? null)}
                  />
                </div>

                <div className="exchange-modal__col-qty">
                  <input
                    type="number"
                    min="1"
                    className="form-control form-control-sm"
                    value={line.quantity}
                    onChange={e => updateLine(line.id, 'quantity', e.target.value)}
                  />
                </div>

                <div className="exchange-modal__col-action">
                  <button
                    type="button"
                    className="modal-icon-btn-danger"
                    onClick={() => removeLine(line.id)}
                    disabled={lines.length <= 1}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button type="button" className="exchange-modal__add-btn mb-3" onClick={addLine}>
            <FontAwesomeIcon icon={faPlus} />
            Ajouter un produit
          </button>

          <hr className="exchange-modal__divider" />

          <div>
            <label className="exchange-modal__notes-label">Notes (optionnel)</label>
            <Textarea
              name="notes"
              control={methods.control}
              placeholder="Remarques..."
            />
          </div>
        </div>
      </FormProvider>
    </Modal>
  );
}
